import React, { useCallback, useEffect } from "react";

/**
 * Call `fn` when the user clicks outside of the element pointed to by `ref`.
 *
 * This is a React hook, which means that use can only use it within a
 * functional component, and it must be used unconditionally. See the
 * documentation at https://reactjs.org/docs/hooks-intro.html for more
 * information.
 *
 * @param ref - A ref to some DOM element. When a click event bubbles up to the
 * document, we check if the target of that event is within the element in this
 * ref.
 * @param fn - A function to be called if the clicked element is not inside the
 * ref'd element.
 */
export default function useBlur(
	ref: React.RefObject<HTMLElement> | React.MutableRefObject<HTMLElement | undefined>,
	fn: (e: MouseEvent) => void,
	capturePhase = false,
) {
	const isMounted = React.useRef<boolean>(false);

	const listener = useCallback(
		(e: MouseEvent) => {
			if (!isMounted.current) {
				return;
			}

			if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) {
				fn(e);
			}
		},
		[isMounted, fn, ref],
	);

	useEffect(() => {
		isMounted.current = true;

		// https://github.com/facebook/react/issues/20074
		const timeoutId = window.setTimeout(() => {
			if (!isMounted.current) return;
			document.addEventListener("click", listener, capturePhase);
		}, 0);

		return () => {
			document.removeEventListener("click", listener, capturePhase);
			clearTimeout(timeoutId);
			isMounted.current = false;
		};
	}, [listener, capturePhase]);
}
